<script>
/**
 * ****************************************************************************
 * @description     :   Input DatePicker Component
 * @created at      :   2024/05/22
 * @created by      :   ANS903 – phuongdt@ans-asia.com
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

import flatPickr from 'vue-flatpickr-component';
import _ from 'lodash-uuid';
import { Japanese } from 'flatpickr/dist/l10n/ja.js';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js';
import 'flatpickr/dist/plugins/monthSelect/style.css';
// import moment from 'moment';

/**
 * Import template
 */
import template from './template.html';
import helpers from '@/utils/helpers';
import moment from 'moment';
import { isArray } from 'lodash';

/**
 * Define content of input datepicker component
 */
var AnsDatePicker = {
    template: template,
    props: {
        id: String,
        label: String,
        name: String,
        itemClass: [Object, String],
        labelClass: String,
        wrapInputClass: {
            type: Object,
            default: () => ({})
        },
        inputClass: [Object, String],
        buttonClass: [Object, String],
        customIconClass: {
            type: [String, Object],
            default: () => ({})
        },
        icon: String,
        placeholder: String,
        value: String,
        modelValue: String,
        isRequired: Boolean,
        isDisabled: Boolean,
        isReadonly: Boolean,
        isAlwaysEnable: {
            type: Boolean,
            default: false
        },
        isTimer: {
            type: Boolean,
            default: false
        },
        showIcon: {
            type: Boolean,
            default: true
        },
        isMonthYear: {
            type: Boolean,
            default: false
        },
        isYearOnly: {
            type: Boolean,
            default: false
        },
        isMonthDayOnly: {
            type: Boolean,
            default: false
        },
        isAllowMonthYear: {
            type: Boolean,
            default: false
        },
        autofocus: {
            type: Boolean,
            default: true
        },
        isHorizontal: {
            type: Boolean,
            default: false
        },
        widthLabel: {
            type: String,
            default: 'width-110-px'
        },
        isAllowInput: { type: Boolean, default: true },
        maxDate: {
            type: String,
            default: ''
        },
        minDate: {
            type: String,
            default: ''
        },
        onChangeEvent: Function,
        onFocusEvent: Function,
        onBlurEvent: Function,
        onClick: Function,
        errors: {
            type: Array
        },
        formatDateOnlyYear: {
            type: Boolean,
            default: false
        },
        tabindex: {
            type: Number,
            default: 1
        }
    },
    components: {
        flatPickr
    },
    emits: ['update:modelValue'],
    data() {
        return {
            newId: '',
            isInputting: false,
            keyRender: 0,
            config_timer: {
                wrap: true,
                allowInput: this.isAllowInput,
                enableTime: true,
                noCalendar: true,
                dateFormat: 'H:i',
                time_24hr: true,
                clickOpens: false,
                locale: Japanese,
                onChange: this.updateInput
            },
            modeDisabled: false
        };
    },
    created() {
        if (
            ![
                'MNG_Order001',
                'MNG_System002',
                'MNG_System004',
                'MNG_Order004',
                'MNG_File001',
                'CUS_Order021',
                'CUS_Billing001',
                'CUS_Client003',
                'CUS_Delivery001'
            ].includes(this.$route.name)
        ) {
            this.modeDisabled = helpers.checkAuthorityMode(this.$route.name) && !this.isAlwaysEnable;
        }
    },
    computed: {
        vewValue: {
            get() {
                return this.getValue(this.modelValue ? this.modelValue : this.value);
            },
            set() {}
        },
        config_datetime() {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const nextYear = new Date();
            nextYear.setFullYear(today.getFullYear() + 1); // Cộng thêm 1 năm
            nextYear.setHours(0, 0, 0, 0);
            const config = {
                wrap: true,
                // eslint-disable-next-line prettier/prettier
                altFormat: this.isMonthYear ? 'Y/m' : this.isYearOnly ? 'Y' : this.isMonthDayOnly ? 'm/d' : 'Y/m/d',
                altInput: true,
                // eslint-disable-next-line prettier/prettier
                dateFormat: this.isMonthYear ? 'Y/m' : this.isYearOnly ? 'Y/m/d' : this.isMonthDayOnly ? 'm/d' : 'Y/m/d',
                allowInput: this.isAllowInput,
                clickOpens: false,
                monthSelectorType: 'static',
                locale: Japanese,
                onOpen: this.openDate,
                onChange: this.updateInput,
                onReady: this.onCalendarReady,
                ...(this.formatDateOnlyYear ? { minDate: today, maxDate: nextYear } : { maxDate: this.maxDate, minDate: this.minDate })
            };
            if (this.isMonthYear) {
                config.plugins = [
                    new monthSelectPlugin({
                        shorthand: true,
                        dateFormat: 'Y/m',
                        altFormat: 'Y/m'
                    })
                ];
            }
            return config;
        },
        maxlength() {
            // eslint-disable-next-line prettier/prettier
            return this.isTimer ? 5 : this.isYearOnly ? 4 : this.isMonthYear ? 7 : this.isMonthDayOnly ? 5 : 10;
        },
        validateError() {
            let message = '';
            if (this.errors) {
                this.errors.forEach((item) => {
                    if (item.name == this.id) {
                        message = item.message;
                    }
                });
            }
            return message;
        },
        customizeItemClass() {
            const baseClass = {
                'item-component': true,
                'flex-col': this.isRequired
            };

            const addClass = {
                ...(this.isHorizontal || this.isRequired ? { 'd-flex': true } : {}),
                ...(this.isHorizontal ? { 'align-items-center': true } : {})
            };

            if (typeof this.itemClass === 'object' && this.itemClass !== null) {
                return { ...baseClass, ...addClass, ...this.itemClass };
            } else if (typeof this.itemClass === 'string' && this.itemClass) {
                return { ...baseClass, ...addClass, [this.itemClass]: true };
            }

            return { ...baseClass, ...addClass };
        },
        customizeInputClassForFlatPickr() {
            const baseClass = {
                'form-control': true,
                'rounded-025': true,
                'input-cal-hidden': true
            };

            const addClass = {
                ...(this.isRequired ? { required: true } : {}),
                ...(this.isTimer
                    ? { 'custom-time-input': true }
                    : this.isMonthYear
                    ? { 'custom-month-year-input': true }
                    : { 'custom-date-input': true }),
                ...(this.isAllowMonthYear ? { 'allow-month-year': true } : {})
            };

            if (typeof this.inputClass === 'object' && this.inputClass !== null) {
                return { ...baseClass, ...addClass, ...this.inputClass };
            } else if (typeof this.inputClass === 'string' && this.inputClass) {
                return { ...baseClass, ...addClass, [this.inputClass]: true };
            }

            return { ...baseClass, ...addClass };
        },
        customizeInputClassForInput() {
            const baseClass = {
                'form-control': true,
                'custom-input': true,
                'rounded-025': true,
                'input-cal-show': true,
                'input-focus-class': true,
                set_boder_input: true,
                'font-size-12-px': true
            };

            const addClass = {
                ...(this.isRequired ? { required: true } : {}),
                ...(this.validateError ? { 'require-input': true } : {}),
                ...(this.vewValue ? { 'bg-white': true } : {})
            };

            if (typeof this.inputClass === 'object' && this.inputClass !== null) {
                return { ...baseClass, ...addClass, ...this.inputClass };
            } else if (typeof this.inputClass === 'string' && this.inputClass) {
                return { ...baseClass, ...addClass, [this.inputClass]: true };
            }

            return { ...baseClass, ...addClass };
        },
        customizeButtonClass() {
            const baseClasses = {
                'input-group-text': true,
                set_boder_left: true
            };

            if (typeof this.buttonClass === 'object') {
                return {
                    ...baseClasses,
                    ...this.buttonClass
                };
            } else if (typeof this.buttonClass === 'string') {
                return {
                    ...baseClasses,
                    [this.buttonClass]: true
                };
            }

            return baseClasses;
        }
    },
    mounted() {
        if (this.id && this.id != '') {
            this.newId = this.id;
        } else {
            this.newId = _.uuid();
        }
    },
    methods: {
        /**
         * convert date for input datepiker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params : value
         * @access : private
         */
        convertTimeString(value) {
            if (value == undefined || value == '' || value == null) {
                return '';
            }
            var reg1 = /^([01][0-9]|2[0-3])([0-5][0-9])$/;
            var reg2 = /^([01][0-9]|2[0-3]):([0-5][0-9])$/;
            var reg3 = /^([0-9])([0-5][0-9])$/;
            var reg4 = /^([0-5][0-9])$/;
            var reg5 = /^([0-9]):([0-5][0-9])$/;
            if (value.match(reg1)) {
                value = value.substring(0, 2) + ':' + value.substring(2, 4);
            }
            if (value.match(reg3)) {
                value = value.substring(0, 1) + ':' + value.substring(1, 3);
            }
            if (value.match(reg4)) {
                value = '00:' + value;
            }
            if (!value.match(reg2) && !value.match(reg5)) {
                value = '';
            }
            //
            return value;
        },
        /**
         * convert date for input datepiker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params : value
         * @access : private
         */
        convertDateString(value) {
            if (value == undefined || value == '' || value == null || isArray(value)) {
                return '';
            }
            var reg1 = /^[0-9]{8}$/;
            var reg2 = /^[0-9]{4}[/.][0-9]{2}[/.][0-9]{2}$/;
            const digits = /^[0-9]{4}$/;
            if (value.match(reg1)) {
                value = value.substring(0, 4) + '/' + value.substring(4, 6) + '/' + value.substring(6);
            }
            if (!value.match(reg1) && !value.match(reg2)) {
                if (value.match(digits)) {
                    value = moment().format('YYYY') + '/' + value.substring(0, 2) + '/' + value.substring(2);
                } else {
                    value = '';
                }
            }
            if (!this.isDate(value)) {
                value = '';
            }
            return value;
        },
        /**
         * convert year/month for input datepicker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params : value
         * @access : private
         */
        convertYearMonthString(value) {
            if (value == undefined || value == '' || value == null) {
                return '';
            }
            var reg1 = /^[0-9]{6}$/;
            var reg2 = /^[0-9]{4}[/.][0-9]{2}$/;
            if (value.match(reg1)) {
                value = value.substring(0, 4) + '/' + value.substring(4, 6);
            }
            if (!value.match(reg1) && !value.match(reg2)) {
                value = '';
            }
            // if month > 12 => clear value
            if (value.substring(5, 7) && value.substring(5, 7) > 12) {
                value = '';
            }
            return value;
        },
        /**
         * convert month/day for input datepicker (MM/DD format)
         * -----------------------------------------
         * @author : DaoNX - 2025/12/16 - create
         * @params : value
         * @access : private
         */
        convertMonthDayString(value) {
            if (value == undefined || value == '' || value == null) {
                return '';
            }
            // Format: MMDD (4 digits) -> MM/DD
            var reg1 = /^[0-9]{4}$/;
            // Format: MM/DD or M/D or M/DD or MM/D
            var reg2 = /^[0-9]{1,2}[/.][0-9]{1,2}$/;
            if (value.match(reg1)) {
                value = value.substring(0, 2) + '/' + value.substring(2, 4);
            }
            if (!value.match(reg1) && !value.match(reg2)) {
                value = '';
            }
            if (value) {
                const parts = value.split(/[/.]/);
                const month = parseInt(parts[0], 10);
                const day = parseInt(parts[1], 10);

                // Validate month (1-12)
                if (month < 1 || month > 12 || day < 1) {
                    value = '';
                } else {
                    // Calculate max days in the given month using current year
                    // new Date(year, month, 0) returns last day of previous month
                    const currentYear = new Date().getFullYear();
                    const maxDay = new Date(currentYear, month, 0).getDate();

                    // Validate day based on calculated max day of month
                    if (day > maxDay) {
                        value = '';
                    } else {
                        // Normalize format to MM/DD
                        value = month.toString().padStart(2, '0') + '/' + day.toString().padStart(2, '0');
                    }
                }
            }
            return value;
        },
        /**
         * convert year/month for input datepicker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params : value
         * @access : private
         */
        convertYearString(value) {
            if (value == undefined || value == '' || value == null) {
                return '';
            }
            value = value.substring(0, 4);
            return value;
        },
        /**
         * Check date type for input datepiker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params : value
         * @access : private
         */
        isDate(string) {
            if (string == '') {
                return true;
            }
            if (string.length == 8) {
                string = string.substring(0, 4) + '/' + string.substring(4, 6) + '/' + string.substring(6);
            }
            var reg =
                /^((19|[2-9][0-9])[0-9]{2})[/.](0[13578]|1[02])[/.]31|((19|[2-9][0-9])[0-9]{2}[/.](01|0[3-9]|1[0-2])[/.](29|30))|((19|[2-9][0-9])[0-9]{2}[/.](0[1-9]|1[0-2])[/.](0[1-9]|1[0-9]|2[0-8]))|((((19|[2-9][0-9])(04|08|[2468][048]|[13579][26]))|2000)[/.](02)[/.]29)$/;
            if (string.match(reg)) {
                return true;
            } else {
                return false;
            }
        },
        /**
         * event keydown on input datepiker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        inputKeydown(e) {
            if (!this.isTimer) {
                if (
                    !(
                        (e.keyCode > 47 && e.keyCode < 58) ||
                        (e.keyCode > 95 && e.keyCode < 106) ||
                        e.keyCode == 116 ||
                        e.keyCode == 13 ||
                        e.keyCode == 46 ||
                        e.keyCode == 37 ||
                        e.keyCode == 39 ||
                        e.keyCode == 35 ||
                        e.keyCode == 36 ||
                        e.keyCode == 8 ||
                        e.keyCode == 9 ||
                        e.ctrlKey ||
                        e.keyCode == 229 ||
                        e.keyCode == 191
                    )
                ) {
                    event.preventDefault();
                    return false;
                }
            } else {
                if (
                    !(
                        (e.keyCode > 47 && e.keyCode < 58) ||
                        (e.keyCode > 95 && e.keyCode < 106) ||
                        e.keyCode == 116 ||
                        e.keyCode == 13 ||
                        e.keyCode == 46 ||
                        e.keyCode == 37 ||
                        e.keyCode == 39 ||
                        e.keyCode == 35 ||
                        e.keyCode == 36 ||
                        e.keyCode == 8 ||
                        e.keyCode == 9 ||
                        e.ctrlKey ||
                        e.keyCode == 229 ||
                        e.keyCode == 186
                    )
                ) {
                    event.preventDefault();
                    return false;
                }
            }
            return true;
        },
        onChange(e) {
            let val = this.getValue(e.target.value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const nextYear = new Date();
            nextYear.setFullYear(today.getFullYear() + 1);
            nextYear.setHours(0, 0, 0, 0);

            if (val && this.formatDateOnlyYear) {
                const inputDate = new Date(val);
                if (inputDate < today || inputDate > nextYear) {
                    val = '';
                }
            }
            if (val === '') {
                this.keyRender++;
            }
            if ((this.vewValue ?? '') != (val ?? '')) {
                this.isInputting = true;
                this.$emit('update:modelValue', val);
                if (this.onChangeEvent) {
                    this.onChangeEvent(val, null, this.$refs['input_' + this.newId]);
                }
            }
        },
        onBlur(e) {
            if (this.onBlurEvent) {
                this.onBlurEvent(e, this.$refs['input_' + this.newId]);
            }
        },
        onDoubleClick(e) {
            if (!e.target.value) {
                e.target.value = moment().format('YYYY/MM/DD');
                this.onChange(e);
            }
        },
        onFocus(e) {
            if (this.onFocusEvent) {
                this.onFocusEvent(e);
            }
            // if (this.isTimer) {
            //     e.target.value = e.target.value.replace(':', '');
            // }
        },
        getValue(input) {
            let val = '';
            if (this.isTimer) {
                val = this.convertTimeString(input);
            } else if (this.isMonthYear || (this.isAllowMonthYear && input?.length < 8)) {
                val = this.convertYearMonthString(input);
            } else if (this.isYearOnly) {
                val = this.convertYearString(input);
            } else if (this.isMonthDayOnly) {
                val = this.convertMonthDayString(input);
            } else {
                val = this.convertDateString(input);
            }
            return val;
        },
        /**
         * update input datepiker
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        // eslint-disable-next-line no-unused-vars
        updateInput(selectedDates, dateStr) {
            // if (this.autofocus) {
            //     this.$refs['input_' + this.newId].focus();
            // }
            if ((this.vewValue ?? '') != (dateStr ?? '') && !this.isInputting) {
                this.$emit('update:modelValue', dateStr);
                if (this.onChangeEvent) {
                    this.onChangeEvent(dateStr, selectedDates, this.$refs['input_' + this.newId]);
                }
            }
            this.isInputting = false;
        },
        openDate(selectedDates, dateStr, instance) {
            instance.jumpToDate(this.minDate);
        },
        onCalendarReady(selectedDates, dateStr, instance) {
            if (this.isMonthDayOnly && instance.calendarContainer) {
                instance.calendarContainer.classList.add('hide-year');
            }
        }
    }
};
export default AnsDatePicker;
</script>

<style lang="scss">
// Hide year selector on flatpickr calendar when using month/day only mode
.flatpickr-calendar.hide-year {
    .flatpickr-current-month {
        .numInputWrapper {
            display: none;
        }

        .cur-month {
            &:before {
                content: none;
            }
        }
    }

    .flatpickr-prev-month,
    .flatpickr-next-month {
        top: 8px;
    }
}
.date-picker {
    max-width: 130px !important;
}
.flex-col {
    flex-direction: column;
}
</style>
